/**
 * FilePondManager - Advanced file upload management system
 * Supports multiple independent FilePond configurations
 * Compatible with Laravel Media Library
 */

import { FilePond } from "./plugins";

// Translation system
const translations = {
    ar: {
        "Drag & Drop your files or": "قم بسحب وإفلات الملفات أو",
        browse: "تصفح",
        "Error parsing server response": "خطأ في تحليل استجابة الخادم",
        "Upload failed": "فشل التحميل",
        "Error parsing error response": "خطأ في تحليل استجابة الخطأ",
        "Failed to connect to server": "فشل الاتصال بالخادم",
        "Failed to delete file": "فشل حذف الملف",
        "Failed to upload file": "فشل تحميل الملف",
        "Error uploading file": "خطأ في رفع الملف",
        OK: "حسناً",
        "Error during FilePond reset": "خطأ أثناء إعادة تعيين FilePond",
        "No elements found for selector": "لم يتم العثور على عناصر للمحدد",
        "No FilePond found for element": "لم يتم العثور على FilePond للعنصر",
        uploading: "جاري التحميل",
        idle: "خامل",
    },
    en: {
        "Drag & Drop your files or": "Drag & Drop your files or",
        browse: "Browse",
        "Error parsing server response": "Error parsing server response",
        "Upload failed": "Upload failed",
        "Error parsing error response": "Error parsing error response",
        "Failed to connect to server": "Failed to connect to server",
        "Failed to delete file": "Failed to delete file",
        "Failed to upload file": "Failed to upload file",
        "Error uploading file": "Error uploading file",
        OK: "OK",
        "Error during FilePond reset": "Error during FilePond reset",
        "No elements found for selector": "No elements found for selector",
        "No FilePond found for element": "No FilePond found for element",
        uploading: "Uploading",
        idle: "Idle",
    },
};

function t(key) {
    // Check if KTUtil exists and has isRTL method
    const isRTL =
        typeof window !== "undefined" &&
        window.KTUtil &&
        typeof window.KTUtil.isRTL === "function"
            ? window.KTUtil.isRTL()
            : document.documentElement.dir === "rtl";

    const lang = isRTL ? "ar" : "en";
    return translations[lang]?.[key] || key;
}

class MediaManager {
    constructor() {
        this.instances = new Map();
        this.pendingUploads = 0;
        this.globalConfig = {
            labelIdle: this._getLocalizedLabelText(),
            credits: false,
            allowDrop: true,
            allowBrowse: true,
            allowImagePreview: true,
            imagePreviewHeight: 80,
            // Client-side resize/transform is opt-in per instance (camera flows
            // only); off here so every other uploader keeps sending originals.
            allowImageResize: false,
            allowImageTransform: false,
            acceptedFileTypes: [
                "image/png",
                "image/jpeg",
                "image/jpg",
                "image/webp",
                "image/bmp",
                "image/tiff",
            ],
        };
    }

    /**
     * Get the appropriate localized text based on page direction
     */
    _getLocalizedLabelText() {
        const actionText1 = t("Drag & Drop your files or");
        const actionText2 = t("browse");
        return `${actionText1} <span class='filepond--label-action'>${actionText2}</span>`;
    }

    /**
     * Get CSRF token
     */
    _getSecurityToken() {
        if (typeof document === "undefined") return "";

        const metaTag = document.querySelector('meta[name="csrf-token"]');
        return metaTag ? metaTag.getAttribute("content") : "";
    }

    /**
     * Create server configuration for file uploads
     * @param {string} uploadUrl - Url for file uploads
     * @param {string} removeUrl - Url for file deletion
     * @returns {object} - Server configuration
     */
    _createServerConfig(uploadUrl, removeUrl) {
        return {
            process: (
                fieldName,
                file,
                metadata,
                load,
                error,
                progress,
                abort
            ) => {
                const xhr = new XMLHttpRequest();
                const formData = new FormData();

                this.pendingUploads++;

                formData.append("file", file);

                xhr.open("POST", uploadUrl, true);
                xhr.setRequestHeader("X-CSRF-TOKEN", this._getSecurityToken());
                xhr.setRequestHeader("Accept", "application/json");
                xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) {
                        progress(
                            e.lengthComputable ? (e.loaded / e.total) * 100 : 0
                        );
                    }
                };

                xhr.onload = () => {
                    this.pendingUploads = Math.max(0, this.pendingUploads - 1);

                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const response = JSON.parse(xhr.responseText);
                            const mediaId =
                                response.name || response.id || null;

                            load(mediaId);
                            this._triggerEvent("mediaUploaded", response);
                        } catch (e) {
                            console.error(
                                t("Error parsing server response"),
                                e
                            );
                            error(t("Error parsing server response"));
                        }
                    } else {
                        let errorMessage = t("Upload failed");

                        try {
                            const response = JSON.parse(xhr.responseText);
                            errorMessage =
                                response.message ||
                                response.error ||
                                errorMessage;
                        } catch (e) {
                            console.error(t("Error parsing error response"), e);
                        }

                        error(errorMessage);
                    }
                };

                xhr.onerror = () => {
                    this.pendingUploads = Math.max(0, this.pendingUploads - 1);
                    error(t("Failed to connect to server"));
                };

                xhr.onabort = () => {
                    this.pendingUploads = Math.max(0, this.pendingUploads - 1);
                };

                xhr.send(formData);

                return {
                    abort: () => {
                        xhr.abort();
                        abort();
                    },
                };
            },

            revert: (mediaId, load, error) => {
                if (!mediaId) {
                    load();
                    return;
                }

                const xhr = new XMLHttpRequest();

                xhr.open("POST", removeUrl, true);
                xhr.setRequestHeader("X-CSRF-TOKEN", this._getSecurityToken());
                xhr.setRequestHeader("Content-Type", "application/json");
                xhr.setRequestHeader("Accept", "application/json");
                xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

                const data = JSON.stringify({
                    name: mediaId,
                });

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        load();
                        this._triggerEvent("mediaDeleted", { mediaId });
                    } else {
                        console.error(
                            t("Failed to delete file"),
                            xhr.responseText
                        );
                        error(t("Failed to delete file"));
                    }
                };

                xhr.onerror = () => {
                    console.error(t("Failed to delete file"));
                    error(t("Failed to delete file"));
                };

                xhr.send(data);
            },

            load: (source, load, error, progress, abort, headers) => {
                // For handling pre-existing files
                if (typeof source === "object" && source.url) {
                    fetch(source.url)
                        .then((response) => response.blob())
                        .then((blob) => load(blob))
                        .catch((err) => {
                            console.error("Error loading file:", err);
                            error("Could not load file");
                        });
                    return;
                }

                // For simple URLs or file paths
                if (typeof source === "string" && source.startsWith("http")) {
                    fetch(source)
                        .then((response) => response.blob())
                        .then((blob) => load(blob))
                        .catch((err) => {
                            console.error("Error loading file:", err);
                            error("Could not load file");
                        });
                    return;
                }

                // For local files or other sources
                load(source);
            },
        };
    }

    /**
     * Dispatch custom event for external listeners
     * @param {string} eventName - Event name
     * @param {object} data - Event data
     * @private
     */
    _triggerEvent(eventName, data = {}) {
        if (typeof document === "undefined") return;

        try {
            const event = new CustomEvent(`filepond:${eventName}`, {
                detail: data,
                bubbles: true,
            });
            document.dispatchEvent(event);
        } catch (e) {
            console.error("Error triggering event:", e);
        }
    }

    /**
     * Update upload status
     * @param {string} status - Upload status
     * @private
     */
    _updateUploadStatus(status) {
        if (typeof document === "undefined") return;

        const uploadStatus = document.getElementById("uploadStatus");
        if (uploadStatus) {
            uploadStatus.value = status;
        }

        this._triggerEvent("uploadStatusChanged", { status });
    }

    /**
     * Handle file upload errors
     * @param {object} error - Error object
     * @param {object} file - File associated with the error
     * @param {string} status - Upload status
     * @private
     */
    _handleError(error, file, status) {
        const errorMessage =
            error.body || error.message || t("Failed to upload file");

        // Check if SweetAlert is available
        if (typeof window !== "undefined" && window.Swal) {
            window.Swal.fire({
                icon: "error",
                title: t("Error uploading file"),
                text: errorMessage,
                confirmButtonText: t("OK"),
            });
        } else {
            // Fallback to alert
            alert(`${t("Error uploading file")}: ${errorMessage}`);
        }

        console.error(t("Error uploading file") + ":", { error, file, status });
    }

    /**
     * Create and merge FilePond configuration
     */
    _createConfig(options, isMultiple, uploadUrl, removeUrl) {
        const serverConfig = this._createServerConfig(uploadUrl, removeUrl);

        const defaultConfig = {
            ...this.globalConfig,
            allowRemove: true,
            allowReplace: true,
            allowMultiple: isMultiple,
            server: serverConfig,
            onaddfile: (error, file) => {
                if (error) {
                    console.error("Error adding file:", error);
                    return;
                }

                this._updateUploadStatus(t("uploading"));
                this.pendingUploads++;
                this._triggerEvent("fileAdded", { error, file });

                if (
                    options.onaddfile &&
                    typeof options.onaddfile === "function"
                ) {
                    options.onaddfile(error, file);
                }
            },
            onprocessfile: (error, file) => {
                this.pendingUploads = Math.max(0, this.pendingUploads - 1);

                const status =
                    this.pendingUploads > 0 ? t("uploading") : t("idle");
                this._updateUploadStatus(status);
                this._triggerEvent("fileProcessed", { error, file });

                if (
                    options.onprocessfile &&
                    typeof options.onprocessfile === "function"
                ) {
                    options.onprocessfile(error, file);
                }
            },
            onerror: (error, file, status) => {
                this._handleError(error, file, status);
                this._triggerEvent("uploadError", { error, file, status });

                if (options.onerror && typeof options.onerror === "function") {
                    options.onerror(error, file, status);
                }
            },
        };

        // If multiple files option
        if (isMultiple) {
            defaultConfig.allowReorder = true;
        } else {
            defaultConfig.onremovefile = (error, file) => {
                if (options.element && options.element.value !== undefined) {
                    options.element.value = "";
                }

                this._triggerEvent("fileRemoved", { error, file });

                if (
                    options.onremovefile &&
                    typeof options.onremovefile === "function"
                ) {
                    options.onremovefile(error, file);
                }
            };
        }

        // Handle acceptedFileTypes properly
        if (options.acceptedFileTypes) {
            if (
                Array.isArray(options.acceptedFileTypes) &&
                options.acceptedFileTypes.length === 0
            ) {
                delete options.acceptedFileTypes;
            }
        }

        // Create final config by merging default with user options
        const finalConfig = { ...defaultConfig };

        // Merge user options, but be careful with callbacks
        Object.keys(options).forEach((key) => {
            if (
                key !== "element" &&
                key !== "onaddfile" &&
                key !== "onprocessfile" &&
                key !== "onerror" &&
                key !== "onremovefile"
            ) {
                finalConfig[key] = options[key];
            }
        });

        return finalConfig;
    }

    /**
     * Create and configure a new FilePond instance
     * @param {string} selector - CSS selector for elements
     * @param {object} options - Configuration options
     * @returns {MediaManager} - Reference to the manager for method chaining
     */
    create(selector, options = {}) {
        if (typeof document === "undefined") {
            console.error("Document is not available");
            return this;
        }

        const elements = document.querySelectorAll(selector);
        if (!elements.length) {
            console.error(
                `${t("No elements found for selector")}: ${selector}`
            );
            return this;
        }

        elements.forEach((element) => {
            try {
                const isMultiple = options.allowMultiple || false;
                const uploadUrl = options.uploadUrl || "/media/upload";
                const removeUrl = options.removeUrl || "/media/delete";

                // Create configuration
                const config = this._createConfig(
                    { ...options, element },
                    isMultiple,
                    uploadUrl,
                    removeUrl
                );

                // Create FilePond instance
                const pond = FilePond.create(element, config);

                // Store reference
                this.instances.set(element, pond);

                // Also store reference on element for compatibility
                element.filepond = pond;
            } catch (error) {
                console.error("Error creating FilePond instance:", error);
            }
        });

        return this;
    }

    /**
     * Create a multi-file field
     * @param {string} selector - CSS selector for elements
     * @param {object} options - Additional configuration options
     * @returns {MediaManager} - Reference for chaining
     */
    createMultiple(selector, options = {}) {
        return this.create(selector, {
            allowMultiple: true,
            uploadUrl: options.uploadUrl || "/media/upload",
            removeUrl: options.removeUrl || "/media/delete",
            acceptedFileTypes: options.acceptedFileTypes || [],
            ...options,
        });
    }

    /**
     * Create a single file field
     * @param {string} selector - CSS selector for elements
     * @param {object} options - Additional configuration options
     * @returns {MediaManager} - Reference for chaining
     */
    createSingle(selector, options = {}) {
        return this.create(selector, {
            allowMultiple: false,
            uploadUrl: options.uploadUrl || "/media/upload",
            removeUrl: options.removeUrl || "/media/delete",
            acceptedFileTypes: options.acceptedFileTypes || [],
            ...options,
        });
    }

    /**
     * Reset FilePond instance(s) for a given selector
     * @param {string} selector - CSS selector for the FilePond elements
     * @returns {MediaManager}
     */
    reset(selector) {
        if (typeof document === "undefined") return this;

        const elements = document.querySelectorAll(selector);
        if (!elements.length) {
            console.error(
                `${t("No elements found for selector")}: ${selector}`
            );
            return this;
        }

        elements.forEach((element) => {
            // Resolve robustly (handles the #id-moved-to-root case).
            const pond = this.instances.get(element) || this.getInstance(selector);

            if (pond) {
                try {
                    pond.removeFiles();
                } catch (e) {
                    console.error(t("Error during FilePond reset"), e);
                }
            } else {
                console.warn(`${t("No FilePond found for element")}`, element);
            }
        });

        return this;
    }

    /**
     * Destroy FilePond instance(s) for a given selector
     * @param {string} selector - CSS selector for the FilePond elements
     * @returns {MediaManager}
     */
    destroy(selector) {
        if (typeof document === "undefined") return this;

        const elements = document.querySelectorAll(selector);
        if (!elements.length) {
            console.error(
                `${t("No elements found for selector")}: ${selector}`
            );
            return this;
        }

        elements.forEach((element) => {
            // Resolve the pond even when FilePond has moved the #id onto its
            // root wrapper (a plain instances.get(element) misses it then).
            const pond = this.instances.get(element) || this.getInstance(selector);
            if (pond) {
                try {
                    pond.destroy();
                    // Delete whichever Map key actually points at this pond.
                    for (const [el, p] of this.instances) {
                        if (p === pond) {
                            this.instances.delete(el);
                            if (el.filepond) {
                                delete el.filepond;
                            }
                        }
                    }
                } catch (e) {
                    console.error("Error destroying FilePond instance:", e);
                }
            } else {
                console.warn(`${t("No FilePond found for element")}`, element);
            }
        });

        return this;
    }

    /**
     * Reset all FilePond instances
     * @returns {MediaManager} - Manager for chaining
     */
    resetAll() {
        this.instances.forEach((pond, element) => {
            try {
                pond.removeFiles();
            } catch (e) {
                console.error(t("Error during FilePond reset"), e);
            }
        });

        return this;
    }

    /**
     * Get list of processed files from FilePond instance
     * @param {string} selector - CSS selector for the element
     * @returns {string|null} - Comma-separated string of file IDs or null
     */
    getFilesFromInstance(selector) {
        const pond = this.getInstance(selector);
        if (!pond) return null;

        try {
            const files = pond.getFiles().filter((file) => file.status === 5); // 5 means PROCESSING_COMPLETE
            return files
                .map((file) => file.serverId || "")
                .filter((id) => id)
                .join(",");
        } catch (e) {
            console.error("Error getting files from instance:", e);
            return null;
        }
    }

    /**
     * Get FilePond instance
     * @param {string} selector - CSS selector for the element
     * @returns {object|null} - FilePond instance or null
     */
    getInstance(selector) {
        if (typeof document === "undefined") return null;

        const element = document.querySelector(selector);
        if (!element) return null;

        let pond = this.instances.get(element);

        if (!pond) {
            try {
                pond = FilePond.find(element);
            } catch (e) {}
        }

        if (!pond && element.filepond) {
            pond = element.filepond;
        }

        // FilePond transfers the original input's #id onto its generated
        // `.filepond--root` wrapper, so a `#id` query returns the ROOT, not the
        // <input> we keyed the Map with. Match by containment: the real input
        // lives inside that root. Without this, getInstance/destroy/reset all
        // miss the pond once it has initialised — which leaks orphaned ponds
        // and lets a stale "processing" instance keep the picker locked.
        if (!pond) {
            for (const [el, p] of this.instances) {
                try {
                    if (el === element || element.contains(el)) {
                        pond = p;
                        break;
                    }
                } catch (e) {}
            }
        }

        return pond;
    }

    /**
     * Check if there are uploads in progress
     * Read from FilePond itself, not from an internal counter that drifts
     * whenever a file is rejected or its upload is aborted.
     *
     * Pass a `selector` to scope the check to a SINGLE pond. A page with many
     * independent pickers (e.g. one FilePond per safety-procedure question)
     * must never gate one picker's lock on the global upload state — otherwise
     * the first picker to finish stays locked forever while a sibling is still
     * uploading, because nothing re-fires its processfile handler afterwards.
     *
     * @param {string|null} selector - Optional CSS selector to scope the check
     * @returns {boolean} - Whether there are active uploads
     */
    isUploading(selector = null) {
        const { PROCESSING, PROCESSING_QUEUED } = FilePond.FileStatus;

        const ponds = selector
            ? [this.getInstance(selector)].filter(Boolean)
            : [...this.instances.values()];

        return ponds.some((pond) => {
            try {
                return pond
                    .getFiles()
                    .some(
                        (file) =>
                            file.status === PROCESSING ||
                            file.status === PROCESSING_QUEUED
                    );
            } catch (e) {
                return false;
            }
        });
    }

    /**
     * Get all active instances
     * @returns {Map} - Map of all active instances
     */
    getAllInstances() {
        return this.instances;
    }

    /**
     * Get pending uploads count
     * @returns {number} - Number of pending uploads
     */
    getPendingUploadsCount() {
        return this.pendingUploads;
    }
}

// Create and export singleton instance
const FilePondManager = new MediaManager();
export { FilePondManager };
